import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import ParticlesBackground from './components/ParticlesBackground';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import ProjectEditor from './pages/ProjectEditor';
import Login from './pages/Login';
import Register from './pages/Register';
import Pricing from './pages/Pricing';
import { useAuth } from './hooks/useAuth';

function App() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Helmet>
        <title>ProtoSunny - Plateforme Photovoltaïque Professionnelle</title>
        <meta name="description" content="Plateforme professionnelle de dimensionnement photovoltaïque avec cartographie interactive et estimation de production" />
        <meta name="keywords" content="photovoltaïque, panneaux solaires, dimensionnement, cartographie, drone, estimation production" />
        <meta property="og:title" content="ProtoSunny - Plateforme Photovoltaïque" />
        <meta property="og:description" content="Plateforme professionnelle de dimensionnement photovoltaïque" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Background animé */}
      <ParticlesBackground />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-sky-900/20 via-purple-900/20 to-solar-900/20 pointer-events-none" />
      
      {/* Contenu principal */}
      <div className="relative z-10">
        <Navbar />
        
        <AnimatePresence mode="wait">
          <Routes>
            <Route 
              path="/" 
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6 }}
                >
                  <HomePage />
                </motion.div>
              } 
            />
            
            <Route 
              path="/login" 
              element={
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                >
                  <Login />
                </motion.div>
              } 
            />
            
            <Route 
              path="/register" 
              element={
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                >
                  <Register />
                </motion.div>
              } 
            />
            
            <Route 
              path="/pricing" 
              element={
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6 }}
                >
                  <Pricing />
                </motion.div>
              } 
            />
            
            {user && (
              <>
                <Route 
                  path="/dashboard" 
                  element={
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Dashboard />
                    </motion.div>
                  } 
                />
                
                <Route 
                  path="/project/:id" 
                  element={
                    <motion.div
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.4 }}
                    >
                      <ProjectEditor />
                    </motion.div>
                  } 
                />
                
                <Route 
                  path="/project/new" 
                  element={
                    <motion.div
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.4 }}
                    >
                      <ProjectEditor />
                    </motion.div>
                  } 
                />
              </>
            )}
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default App; 